/**
 * Service Worker Registration
 *
 * This file handles service worker registration and updates
 */

import { Workbox } from 'workbox-window';
import { createLogger } from './utils/logger';

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          log.info('App is being served cache-first by a service worker');
        });
      } else {
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  if ('serviceWorker' in navigator) {
    const wb = new Workbox(swUrl);

    wb.addEventListener('installed', (event) => {
      if (event.isUpdate) {
        if (config && config.onUpdate) {
          config.onUpdate(wb.getSW() as any);
        }
        // Show a prompt to reload the page
        if (window.confirm('New version available! Click OK to reload.')) {
          wb.addEventListener('controlling', () => {
            window.location.reload();
          });
          wb.messageSkipWaiting();
        }
      } else {
        if (config && config.onSuccess) {
          config.onSuccess(wb.getSW() as any);
        }
        log.info('Content cached for offline use');
      }
    });

    wb.addEventListener('waiting', () => {
      log.info('New service worker is waiting to activate');
    });

    wb.addEventListener('controlling', () => {
      log.debug('Service worker is now controlling the page');
    });

    wb.addEventListener('activated', (event) => {
      if (!event.isUpdate) {
        log.info('Service worker activated for the first time');
      }
    });

    wb.register()
      .then((registration) => {
        log.info('Service worker registered', registration);
      })
      .catch((error) => {
        log.error('Service worker registration failed', error);
      });
  }
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      log.warn('No internet connection found. App is running in offline mode.');
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        log.error('Failed to unregister service worker', error);
      });
  }
}
const log = createLogger('serviceWorker');
